/**
 * What a selection on the canvas is ABOUT, as a set of canvas node ids.
 *
 * Used to dim everything else, and — in the `focus` display mode — to decide which rule
 * labels and programs are drawn at all. The relation is the eligibility chain
 * question → rule → program, followed in whichever direction the selection sits:
 *
 *   a question  → itself, its rules, the programs they grant
 *   a rule      → its owner, itself, the programs it grants
 *   a program   → itself, every rule that grants it, those rules' owners
 *
 * Navigation is deliberately not followed: the point is "where does this program come
 * from", and pulling in next-question neighbours would light up half the tree.
 */
import type { Projection } from './projection';

export function focusSetOf(p: Projection, selectedId: string | null): Set<string> | null {
  if (!selectedId) return null;
  const node = p.nodes.find((n) => n.id === selectedId);
  if (!node) return null;
  const out = new Set<string>([selectedId]);
  const rulesOf = (ownerId: string) =>
    p.nodes.filter((n) => n.kind === 'rule' && n.data.ownerNodeId === ownerId);
  const programsOfRule = (ruleId: string) =>
    p.edges.filter((e) => e.kind === 'rule-out' && e.source === ruleId).map((e) => e.target);

  if (node.kind === 'question') {
    const rules = rulesOf(node.id);
    // A question with no eligibility rules has nothing to focus on; dimming the whole
    // canvas for it would only hide the context.
    if (rules.length === 0) return null;
    for (const r of rules) {
      out.add(r.id);
      for (const pid of programsOfRule(r.id)) out.add(pid);
    }
  } else if (node.kind === 'rule') {
    out.add(node.data.ownerNodeId);
    for (const pid of programsOfRule(node.id)) out.add(pid);
  } else {
    for (const rid of node.data.grantedBy ?? []) {
      out.add(rid);
      const r = p.nodes.find((n) => n.id === rid);
      if (r) out.add(r.data.ownerNodeId);
    }
  }
  return out;
}
